import type { InternalRole } from "@/lib/roles";

export type AppPageDestination = {
	app: "internal" | "customer";
	path: string;
	label: string;
	hint?: string;
	roles?: readonly InternalRole[];
};

// Canonical product destinations also work from the isolated design preview.
const APP_ORIGINS = {
	internal: "https://cherry.anthemcoffeeimports.com",
	customer: "https://anthemcoffeeimports.com",
} as const;

export function getAppPageHref(destination: AppPageDestination): string {
	const origin = APP_ORIGINS[destination.app];
	const url = new URL(destination.path, origin);
	if (!destination.path.startsWith("/") || url.origin !== origin) {
		throw new Error("Changelog page links must stay within the selected app");
	}
	return url.href;
}

export const APP_PAGES = {
	adminUsers: { app: "internal", path: "/admin?tab=users", label: "Open Users in Cherry" },
	customerPayment: { app: "customer", path: "/dashboard/payment", label: "Open customer page", hint: "The customer needs to be signed in to their own account." },
	customerOrders: { app: "customer", path: "/dashboard/orders", label: "Open customer orders", hint: "The customer needs to be signed in, then choose the order to review." },
	storageOrders: { app: "internal", path: "/logistics/orders?view=modern", label: "Open orders in Cherry", hint: "Choose the order, then open its Customer storage panel.", roles: ["admin", "sales", "warehouse", "logistics"] },
	salesAnalytics: { app: "internal", path: "/sales/analytics?orderScope=orders", label: "Open sales analytics", hint: "Select a client, then choose Client Report.", roles: ["admin", "sales"] },
	inventory: { app: "internal", path: "/inventory/cargos", label: "Open inventory lots" },
	bills: { app: "internal", path: "/admin/accounting/bills", label: "Open bill review", hint: "Choose a bill to inspect its source document." },
	crm: { app: "internal", path: "/sales/crm/opportunities", label: "Open CRM", hint: "Choose an opportunity, then find Add activity.", roles: ["admin", "sales"] },
	campaigns: { app: "internal", path: "/marketing/campaigns", label: "Open campaigns" },
	inbound: { app: "internal", path: "/logistics/inbound-deliveries", label: "Open inbound deliveries" },
} as const satisfies Record<string, AppPageDestination>;
